import { useEffect, useState } from 'react';
import { ProfessionalSummaryDataTable } from '@frontend/components/pages/professionalSummaryData/ProfessionalSummaryDataTable';
import type { IMedication } from '../../../../../interfaces/medication';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { fetchJson } from '../../../../../../store/fetch';
import { translateKey } from '../../../../../../translate';
import { FormParagraph, TableBody, TableHead, TableRow } from '../../../../../elements/common';
import { FormLabel } from '../../../../../elements/forms/FormLabel';
import { FormSection } from '../../../../../elements/forms/formSection/FormSection';
import { HorizontalFormGroupRead } from '../../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../../elements/forms/InputContainer';
import { isCurrentMedication } from './medications';
import { MedicationsTableHeader } from './MedicationsTableHeader';
import { MedicationsTableRow } from './MedicationsTableRow';

interface IMedicationsReadProps {
    entity: ProcessEntity.request | ProcessEntity.procedure;
    entityId: string;
    isDirectProcedureAdded?: boolean;
    onContentLoaded?: (hasContent: boolean) => Promise<void>;
    patientUsesMedication: boolean | null;
}

export function MedicationsRead(props: IMedicationsReadProps) {
    const [medications, setMedications] = useState<IMedication[]>([]);
    const itemsPerPage = 50;

    useEffect(() => {
        loadMedications();
    }, []);

    if (!medications || medications.length === 0) {
        return null;
    }

    return (
        <FormSection title={translateKey('medications.medication')}>
            <HorizontalFormGroupRead>
                <FormLabel>{translateKey('medications.patientUsesMedication')}</FormLabel>
                <InputContainer>
                    <FormParagraph>
                        {translateKey(`common.${props.patientUsesMedication ? 'yes' : 'no'}`)}
                    </FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            {medications.length > 0 && (
                <ProfessionalSummaryDataTable isRead={true}>
                    <TableHead>
                        <TableRow>
                            <MedicationsTableHeader />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {medications.map((item: any) => (
                            <TableRow
                                highlight={isCurrentMedication(item)}
                                key={item.id}
                            >
                                <MedicationsTableRow medication={item} />
                            </TableRow>
                        ))}
                    </TableBody>
                </ProfessionalSummaryDataTable>
            )}
        </FormSection>
    );

    async function loadMedications() {
        let url;
        if (props.entity === ProcessEntity.procedure) {
            url = `/api/procedures/${props.entityId}/procedureMedications`;
        } else {
            url = `/api/requests/${props.entityId}/requestMedications`;
        }
        url += '?limit=' + itemsPerPage;

        const results = await fetchJson(url, 'GET');
        setMedications(results.json.items);
    }
}
